/**
 * The Excise-Tax (Form 4180) wizard shell (03-17). One Formik wraps the whole
 * 8-section interview + the generate step so tab/step navigation never loses data
 * and the shared `FormEditorActions` autosaves the entire `data` blob. The active
 * step is driven by the route `:step` param (owned by the page); the four
 * line-item lists are child tables edited through their own modals (not the blob).
 */

import { Form, Formik } from "formik";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Button } from "@/components/ui/button";
import type { Client } from "@/features/clients/types";
import { FormEditorActions } from "@/features/forms/components/form-editor-actions";
import type { FormSubmission } from "@/features/forms/types";
import { WorkflowRail } from "@/features/workflows/components/workflow-rail";
import { EXCISE_STEPS, exciseStepIndex, type WorkflowStep } from "@/features/workflows/types";
import type { ResolutionWorkflow } from "@/features/workflows/types";
import { schema4180, toInitialValues4180 } from "./fields";
import { ExciseGenerateStep } from "./components/excise-generate-step";
import {
  ExciseSection1,
  ExciseSection2,
  ExciseSection3,
  ExciseSection4,
  ExciseSection5,
  ExciseSection6,
  ExciseSection7,
  ExciseSection8,
} from "./components/excise-sections";

interface Props {
  client: Client;
  caseId: string;
  submission: FormSubmission;
  onSubmissionChange: (s: FormSubmission) => void;
  workflow: ResolutionWorkflow;
  onWorkflowChange: (w: ResolutionWorkflow) => void;
  activeStep: WorkflowStep;
  onSelectStep: (step: WorkflowStep) => void;
}

export function ExciseWizard({
  client,
  caseId,
  submission,
  onSubmissionChange,
  workflow,
  onWorkflowChange,
  activeStep,
  onSelectStep,
}: Props) {
  const idx = exciseStepIndex(activeStep);

  function renderStep() {
    switch (activeStep) {
      case "section1":
        return <ExciseSection1 client={client} />;
      case "section2":
        return <ExciseSection2 submissionId={submission.id} />;
      case "section3":
        return <ExciseSection3 />;
      case "section4":
        return <ExciseSection4 submissionId={submission.id} />;
      case "section5":
        return <ExciseSection5 />;
      case "section6":
        return <ExciseSection6 />;
      case "section7":
        return <ExciseSection7 />;
      case "section8":
        return <ExciseSection8 />;
      case "generate":
        return (
          <ExciseGenerateStep
            clientId={client.id}
            caseId={caseId}
            submission={submission}
            workflow={workflow}
            onCompleted={onWorkflowChange}
          />
        );
      default:
        return <ExciseSection1 client={client} />;
    }
  }

  return (
    <Formik
      initialValues={toInitialValues4180(submission.data)}
      validationSchema={schema4180}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <WorkflowRail
            currentStep={activeStep}
            onSelect={onSelectStep}
            determination={null}
            steps={EXCISE_STEPS}
            title="Excise Tax (Form 4180)"
          />
          <div className="grid gap-6">
            <div>
              <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                Step {idx + 1} of {EXCISE_STEPS.length}
              </p>
              <h3 className="text-lg font-semibold">{EXCISE_STEPS[idx]!.label}</h3>
            </div>

            {renderStep()}

            <div className="flex items-center justify-between border-t pt-4">
              <Button
                type="button"
                variant="outline"
                disabled={idx === 0}
                onClick={() => onSelectStep(EXCISE_STEPS[idx - 1]!.key)}
              >
                <FiChevronLeft className="size-4" /> Back
              </Button>
              <Button
                type="button"
                disabled={idx === EXCISE_STEPS.length - 1}
                onClick={() => onSelectStep(EXCISE_STEPS[idx + 1]!.key)}
              >
                Next <FiChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
